export default defineNuxtPlugin(nuxtApp => {
	if (import.meta.server) {
		nuxtApp.vueApp.directive('interpolation', {});
		return;
	}

	const config = useAppConfig();
	const modal = useModal();
	const page = usePage();
	const {emit} = useEventBus();
	const router = nuxtApp.$router;
	const sHost = nuxtApp.$host;
	let observers = new Map(); // store observers for cleanup

	// hijack link click and process as local or external
	async function navigate(e) {
		// use event delegation - check if clicked element is a link or inside a link
		const link = e.target.closest('a');
		if (!link) return;

		// check if the link should be handled
		if (!shouldHandleLink(link)) return;

		const item = link;
		const url = new URL(link.href);
		const mode = url.searchParams.get('mode');
		const linkClass = link.getAttribute('class');

		// close modals if clicked link is inside a modal
		if (Object.keys(modal.activeModals()).length > 0 && mode != 'quick') {
			modal.close();
		}

		// if link has ?mode parameter, open modal defined in parameter
		// if mode is 'quick' then fetch page from the link url and open 'quick' modal
		if (mode) {
			e.preventDefault();
			const pathname = url.pathname;

			// if mode is 'quick' then fetch page and open modal
			if (mode == 'quick') {
				const res = await page.fetch({'slug': pathname});
				if (!res) return null;
				modal.open('quick', res);
				return res;
			}

			// open custom modal
			modal.open(mode);
			return;
		}

		// if link has class 'fancybox' then open modal with gallery
		if (linkClass?.includes('fancybox')) {
			e.preventDefault();
			const gallery = link.getAttribute('rel');
			let items = [];
			const startIndex = link.getAttribute('data-index') ? link.getAttribute('data-index') : 0;

			if (gallery) {
				// if rel attribute is defined, get all elements with rel set to the same gallery
				const galleryElements = document.querySelectorAll(`a[rel="${gallery}"]`);
				galleryElements.forEach((item, i) => {
					if (item.href) {
						const img = item.querySelector('img');
						items.push({
							id: `item-${i}`,
							title: img?.alt ? img.alt : item.title,
							url: item.href,
							thumb: item.getAttribute('data-thumb'),
							type: item.href.includes('youtube.com') ? 'youtube' : 'image',
						});
					}
				});
			} else {
				// if rel attribute is not defined, get only the current link
				if (link.href) {
					const img = link.querySelector('img');
					items.push({
						id: 'item-0',
						title: img?.alt ? img.alt : link.title,
						url: link.href,
						thumb: link.getAttribute('data-thumb'),
						type: link.href.includes('youtube.com') ? 'youtube' : 'image',
					});
				}
			}

			// open modal with name defined in data-modal attribute. If not defined, open 'quick' modal
			const modalName = link.getAttribute('data-modal') ? link.getAttribute('data-modal') : 'quick';
			modal.open(modalName, {
				'items': items,
				'startIndex': Number(startIndex),
			});
			return;
		}

		// link with target="_blank" - allow natural browser behavior
		if (link.getAttribute('target') === '_blank') {
			return;
		}

		// internal link
		if (item.origin === sHost || item.host === window.location.host) {
			e.preventDefault();
			if (router?.currentRoute?.value?.path === url.pathname) emit('catalogProductsUpdate');
			const route = url.href.replace(item.origin, ''); // remove origin from url but keep query params
			router.push(route);
		}
	}

	// check if link should be handled by the directive
	function shouldHandleLink(link) {
		// check if the link has an href attribute
		if (!link.hasAttribute('href')) {
			return false;
		}

		// ignore links with file extension (except fancybox)
		const href = link.getAttribute('href');
		if (/\.[^/\s]+$/.test(href) && !link.classList.contains('fancybox')) {
			return false;
		}

		return true;
	}

	// process all links in element (add noopener to _blank links)
	function processLinks(el) {
		const allLinks = el.getElementsByTagName('a');

		for (let i = 0; i < allLinks.length; i++) {
			const link = allLinks[i];

			// for improved security `rel="noopener"` will be added automatically if target is `_blank`
			const target = link.getAttribute('target');
			if (target && target === '_blank') {
				const rel = link.getAttribute('rel') || '';
				const attributes = rel.includes('noopener') ? rel : rel + ' noopener';
				link.setAttribute('rel', attributes);
			}
		}
	}

	// setup MutationObserver to watch for dynamically added links
	function setupObserver(el) {
		// clean up existing observer for this element if any
		if (observers.has(el)) {
			const existingObserver = observers.get(el);
			existingObserver.disconnect();
			// clear pending timeout if exists
			if (existingObserver.throttleTimeout) {
				clearTimeout(existingObserver.throttleTimeout);
			}
		}

		// throttle observer callback to reduce CPU usage on frequent DOM changes
		let timeout;
		const throttledCallback = mutations => {
			if (timeout) return; // ignore if already scheduled

			timeout = setTimeout(() => {
				// process all accumulated mutations at once
				mutations.forEach(mutation => {
					// check if new nodes were added
					if (mutation.addedNodes.length) {
						mutation.addedNodes.forEach(node => {
							// process new links if node is an element
							if (node.nodeType === 1) {
								// ELEMENT_NODE
								// if added node is a link itself
								if (node.tagName === 'A') {
									processLinks(node.parentElement);
								}
								// if added node contains links
								else if (node.getElementsByTagName) {
									processLinks(node);
								}
							}
						});
					}
				});
				observer.throttleTimeout = null;
				timeout = null;
			}, 150); // process max once per 150ms
			// store timeout reference on observer for cleanup
			observer.throttleTimeout = timeout;
		};

		const observer = new MutationObserver(throttledCallback);
		// initialize throttleTimeout property
		observer.throttleTimeout = null;

		// start observing the element for changes
		observer.observe(el, {
			childList: true,
			subtree: true,
		});

		// store observer for cleanup
		observers.set(el, observer);
	}

	// wrap each .table element with a div.table-wrapper
	function wrapTables() {
		const allTables = document.querySelectorAll('.table');
		if (!allTables?.length) return;
		const tables = [...allTables].filter(function (table) {
			return !table.parentNode.classList.contains('table-wrapper');
		});

		if (tables.length > 0) {
			for (let i = 0; i < tables.length; i++) {
				if (!tables[i].parentNode.classList.contains('table-wrapper')) {
					const tableWrapper = document.createElement('div');
					tableWrapper.classList.add('table-wrapper');
					tables[i].parentNode.insertBefore(tableWrapper, tables[i]);
					tableWrapper.appendChild(tables[i]);
				}
			}
		}
	}

	// load and execute external scripts from dynamic content
	let scriptTimeouts = [];
	let isAppHydrated = false; // track if app finished initial hydration
	let currentPath = ''; // track current route path

	// mark app as hydrated after first client-side navigation
	if (router) {
		router.afterEach((to, from) => {
			// if we're navigating from one route to another, app is hydrated
			if (from.name !== undefined) {
				isAppHydrated = true;
			}
			currentPath = to.path;
		});
		// set initial path
		currentPath = router.currentRoute.value.path;
	}

	function loadExternalScripts(el, isInitialMount = false) {
		const scripts = el.querySelectorAll('script');
		if (!scripts?.length) return;

		const {addScript} = useMeta();

		scripts.forEach((script, index) => {
			// handle external scripts with src attribute
			if (script.src) {
				// create hash of src URL to avoid special characters in key
				const srcHash = btoa(script.src).replace(/[^a-zA-Z0-9]/g, '');
				const scriptKey = `dynamic-script-${srcHash}`;

				// check if script with same key is already loaded to avoid duplicates
				const existingScript = document.querySelector(`script[data-key="${scriptKey}"]`);
				if (existingScript) {
					script.remove();
					return;
				}

				// create placeholder div element at script location
				const placeholder = document.createElement('div');
				placeholder.setAttribute('data-script-placeholder', scriptKey);
				placeholder.style.display = 'contents'; // doesn't affect layout

				// replace script with placeholder
				script.parentNode.insertBefore(placeholder, script);

				// prepare script configuration for addScript
				const scriptConfig = {
					key: scriptKey,
					src: script.src,
					type: script.type || 'text/javascript',
					appendTo: `[data-script-placeholder="${scriptKey}"]`,
				};

				// copy other attributes if present
				Array.from(script.attributes).forEach(attr => {
					if (attr.name !== 'src' && attr.name !== 'type') {
						if (attr.name.startsWith('data-')) {
							if (!scriptConfig.dataAttributes) scriptConfig.dataAttributes = {};
							scriptConfig.dataAttributes[attr.name.replace('data-', '')] = attr.value;
						} else {
							scriptConfig[attr.name] = attr.value;
						}
					}
				});

				// remove original script element
				script.remove();

				// load script using addScript with delay to ensure DOM is ready
				const timeoutId = setTimeout(() => {
					nextTick(() => {
						addScript(scriptConfig);
					});

					// remove timeout from array after execution
					const index = scriptTimeouts.indexOf(timeoutId);
					if (index > -1) scriptTimeouts.splice(index, 1);
				}, 500);

				// store timeout ID for cleanup
				scriptTimeouts.push(timeoutId);
			}
			// handle inline scripts (scripts without src attribute)
			// skip inline scripts on initial SSR mount to avoid double execution
			else if (script.textContent && !isInitialMount) {
				// create unique hash from script content to avoid duplicates
				const contentHash = btoa(script.textContent.substring(0, 100)).replace(/[^a-zA-Z0-9]/g, '');
				const scriptKey = `inline-script-${contentHash}`;

				// check if this inline script was already executed on this element
				if (script.hasAttribute('data-executed')) {
					script.remove();
					return;
				}

				const scriptContent = script.textContent;
				const scriptType = script.type || 'text/javascript';
				const scriptAttributes = Array.from(script.attributes);
				const parentNode = script.parentNode;

				// mark as executed before removing
				script.setAttribute('data-executed', 'true');

				// remove original script element
				script.remove();

				// execute inline script with delay to ensure DOM and external scripts are ready
				// longer delay than external scripts to ensure they are loaded first
				const timeoutId = setTimeout(() => {
					const newScript = document.createElement('script');
					newScript.type = scriptType;
					newScript.textContent = scriptContent;
					newScript.setAttribute('data-key', scriptKey);
					newScript.setAttribute('data-executed', 'true');

					// copy other attributes if present
					scriptAttributes.forEach(attr => {
						if (attr.name !== 'type' && attr.name !== 'data-executed') {
							newScript.setAttribute(attr.name, attr.value);
						}
					});

					// append new script to trigger execution
					parentNode.appendChild(newScript);

					// remove timeout from array after execution
					const index = scriptTimeouts.indexOf(timeoutId);
					if (index > -1) scriptTimeouts.splice(index, 1);
				}, 1500);

				// store timeout ID for cleanup
				scriptTimeouts.push(timeoutId);
			}
		});
	}

	nuxtApp.vueApp.directive('interpolation', {
		updated(el) {
			processLinks(el);
			// on update, always treat as router navigation (not initial mount)
			loadExternalScripts(el, false);
			//wrapTables();
		},
		mounted(el) {
			// use event delegation - add single listener to parent element
			el.addEventListener('click', navigate, false);

			// process existing links
			processLinks(el);

			// setup observer for dynamically added content
			setupObserver(el);

			// wrap tables
			wrapTables();

			// load external scripts
			// execute scripts only after app is hydrated (after first navigation)
			// on initial SSR load, skip inline scripts as they're already in HTML
			loadExternalScripts(el, !isAppHydrated);
		},
		beforeUnmount(el) {
			// remove delegated event listener
			el.removeEventListener('click', navigate, false);

			// disconnect observer and clear pending timeouts
			if (observers.has(el)) {
				const observer = observers.get(el);
				observer.disconnect();
				// clear pending throttle timeout if exists
				if (observer.throttleTimeout) {
					clearTimeout(observer.throttleTimeout);
				}
				observers.delete(el);
			}

			// clear all pending script timeouts to prevent memory leaks
			scriptTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
			scriptTimeouts = [];

			// remove dynamically added scripts when unmounting
			const dynamicScripts = el.querySelectorAll('script[data-key^="dynamic-script-"], script[data-key^="inline-script-"]');
			dynamicScripts.forEach(script => script.remove());

			// remove script placeholders
			const placeholders = el.querySelectorAll('[data-script-placeholder]');
			placeholders.forEach(placeholder => placeholder.remove());
		},
	});
});
